import { useState, useEffect, useRef } from 'react';
import { useRestaurantId } from '../hooks/useRestaurantId';
import { useTableAuth } from '../hooks/useTableAuth';
import { useApp } from '../context/AppContext';
import { OrderItem } from '../types';
import ErrorModal from './ErrorModal';
import { ApiError } from '../services/http';
import { useAuth } from '../hooks/useAuth';
import { authApiService } from '../services/auth';
import { storageService } from '../services/storage';
import { useSelectedRestaurant } from '../context/SelectedRestaurantContext';
import { api } from '../services/api';

interface CartModalProps {
  visible: boolean;
  onClose: () => void;
  onPlaceOrder: () => void;
}

export default function CartModal({ visible, onClose, onPlaceOrder }: CartModalProps) {
  const { restaurantId } = useRestaurantId();
  const { tableData } = useTableAuth();
  const { cart, placeOrder, setShowCartModal, updateCartItem, removeFromCart } = useApp();
  const { user } = useAuth();
  const { selectedRestaurantId } = useSelectedRestaurant();
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [errorModal, setErrorModal] = useState<{ open: boolean; title?: string; message: string; details?: string }>({ open: false, message: '' });
  const [showLoginRequired, setShowLoginRequired] = useState(false);
  const [pendingAfterLogin, setPendingAfterLogin] = useState(false);
  const loginPromptedRef = useRef(false);
  const isMenuWithoutQr = !tableData?.tableSigned;
  const PUBLIC_ORDER_QUEUE_MY_ORDERS_KEY = 'public_order_queue_my_orders';

  // Actualizar el estado del modal en el contexto
  useEffect(() => {
    setShowCartModal(visible);
  }, [visible, setShowCartModal]);

  // Usar el carrito real del contexto
  const cartItems = cart;

  // Limpiar notas globales cuando el carrito queda vacío (orden enviada)
  useEffect(() => {
    if (cartItems.length === 0 && specialInstructions) {
      setSpecialInstructions('');
      storageService.removeItem('app_cart_ctx' as any).catch(() => {});
    }
  }, [cartItems.length, specialInstructions]);

  // Si se marcó reintento tras login, esperar a que user esté disponible y enviar
  useEffect(() => {
    if (pendingAfterLogin && user) {
      // Evitar loop
      setPendingAfterLogin(false);
      // Resetear guard para futuros flujos
      loginPromptedRef.current = false;
      // Reintentar envío sin volver a pedir login
      handlePlaceOrder(true);
    }
  }, [pendingAfterLogin, user]);

  const formatPrice = (price: number) => {
    return `$${price.toFixed(2)}`;
  };

  const getTotalAmount = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    if (newQuantity < 1) {
      // Si la cantidad es menor a 1, eliminar el item del carrito
      removeFromCart(itemId);
      return;
    }
    
    // Actualizar la cantidad del item en el carrito
    updateCartItem(itemId, newQuantity);
  };

  const handlePlaceOrder = async (skipLoginCheck: boolean = false) => {
    // En menú SIN QR, la dirección es obligatoria (se guarda en notes).
    if (isMenuWithoutQr && !(specialInstructions || '').trim()) {
      setErrorModal({
        open: true,
        title: 'Dirección requerida',
        message: 'Para enviar un pedido sin QR necesitas ingresar la dirección de envío.',
      });
      return;
    }

    // Si no está autenticado, pedir login primero
    if (!user && !skipLoginCheck) {
      if (loginPromptedRef.current) {
        return;
      }
      try {
        // Guardar respaldo del carrito por si el popup modifica el estado
        await storageService.setItem('app_cart_backup', JSON.stringify(cartItems));
        await storageService.setItem('app_cart_ctx', JSON.stringify({ restaurantId: restaurantId || tableData?.restaurantId || '', specialInstructions }));
      } catch(_) {}
      setShowLoginRequired(true);
      loginPromptedRef.current = true;
      return;
    }

    // Determinar restaurante objetivo (mesa o selección / URL)
    const targetRestaurantId = tableData?.restaurantId || restaurantId || selectedRestaurantId;
    
    if (!targetRestaurantId) {
      setErrorModal({ open: true, title: 'Restaurante no identificado', message: 'No se pudo identificar el restaurante' });
      return;
    }

    setIsPlacingOrder(true);
    try {
      let tableSigned = tableData?.tableSigned;
      // Si no hay mesa escaneada, pedir token de mesa virtual (#0) para este restaurante
      if (!tableSigned) {
        const tokenResp = await api.getVirtualTableToken(targetRestaurantId);
        tableSigned = tokenResp?.token;
      }

      if (!tableSigned) {
        setErrorModal({ open: true, title: 'No se pudo crear el pedido', message: 'No se pudo obtener un token de mesa válido para crear la orden' });
        return;
      }

      console.log('📋 Creando orden desde web:', {
        targetRestaurantId,
        tableData: tableData || { restaurantId: targetRestaurantId, tableSigned: 'virtual' },
        specialInstructions,
        items: cartItems,
        total: getTotalAmount()
      });

      // Crear la orden usando el contexto (mismo flujo que React Native)
      const orderData = {
        tableSigned, // Mesa real (QR) o mesa virtual (#0)
        notes: (specialInstructions || '').trim(),
      };

      const order = await placeOrder(orderData);
      
      console.log('✅ Orden creada exitosamente:', order);

      // En menú sin QR, guardar el ID de la orden para poder identificarla en "Estado de orden".
      // Se guarda por restaurante y se conserva un pequeño historial (por si se crean varias).
      try {
        const raw = await storageService.getItem(PUBLIC_ORDER_QUEUE_MY_ORDERS_KEY);
        let map: any = {};
        try {
          map = raw ? JSON.parse(raw) : {};
        } catch {
          map = {};
        }
        const prev = map?.[targetRestaurantId];
        const prevArr = Array.isArray(prev) ? prev : (typeof prev === 'string' ? [prev] : []);
        const next = [order?.id, ...prevArr].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).slice(0, 8);
        map[targetRestaurantId] = next;
        await storageService.setItem(PUBLIC_ORDER_QUEUE_MY_ORDERS_KEY, JSON.stringify(map));
      } catch {
        // noop
      }

      setSpecialInstructions('');
      await storageService.removeItem('app_cart' as any);
      await storageService.removeItem('app_cart_backup' as any);
      await storageService.removeItem('app_cart_ctx' as any);
      loginPromptedRef.current = false;
      setPendingAfterLogin(false);
      
      onPlaceOrder();
      onClose();
    } catch (error: any) {
      console.error('Error enviando orden:', error);
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setErrorModal({ open: true, title: 'No se pudo enviar el pedido', message: error.message });
      } else {
        setErrorModal({ open: true, title: 'Error del servidor', message: 'Error al enviar la orden. Por favor, intenta de nuevo.' });
      }
    } finally {
      setIsPlacingOrder(false);
    }
  };



  const renderCartItem = (item: OrderItem) => (
    <div key={item.id} className="bg-white rounded-xl p-4 mb-3 shadow-sm border border-gray-100">
      <div className="mb-3">
        <h3 className="text-base font-bold text-gray-800 mb-1">{item.menuItem.name}</h3>
        <p className="text-sm text-gray-600 mb-1">{formatPrice(item.menuItem.price)}</p>
        {item.specialInstructions && (
          <p className="text-xs text-orange-600 italic">{item.specialInstructions}</p>
        )}
      </div>
      
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <button
            className="bg-[#f7ebd6] text-[#2C4358] w-8 h-8 rounded-full flex items-center justify-center hover:bg-[#f0e0c0] transition-colors border-2 border-[#f7ebd6]/50 hover:border-[#f0e0c0]"
            onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
          >
            <span className="text-sm font-bold">-</span>
          </button>
          <span className="mx-3 text-base font-bold text-gray-800">{item.quantity}</span>
          <button
            className="bg-[#f7ebd6] text-[#2C4358] w-8 h-8 rounded-full flex items-center justify-center hover:bg-[#f0e0c0] transition-colors border-2 border-[#f7ebd6]/50 hover:border-[#f0e0c0]"
            onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
          >
            <span className="text-sm font-bold">+</span>
          </button>
        </div>
        <span className="text-base font-bold text-blue-600">
          {formatPrice(item.price * item.quantity)}
        </span>
      </div>
    </div>
  );

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-gray-50 w-full sm:max-w-lg max-h-[90vh] sm:max-h-[80vh] flex flex-col relative rounded-t-2xl sm:rounded-2xl overflow-hidden">
        <button
          type="button"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 border border-gray-200 text-gray-700 flex items-center justify-center hover:bg-white disabled:opacity-60"
          onClick={() => !isPlacingOrder && onClose()}
          disabled={isPlacingOrder}
          aria-label="Cerrar"
          title="Cerrar"
        >
          ✕
        </button>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-5 pt-5 pb-2 sm:hidden">
            <div className="text-lg font-bold text-[#2C4358]">Tu Pedido</div>
          </div>
          {cartItems.length === 0 ? (
            <div className="flex-1 flex flex-col justify-center items-center p-10">
              <span className="text-6xl mb-4">🛒</span>
              <h3 className="text-lg font-bold text-gray-600 mb-2">Tu carrito está vacío</h3>
              <p className="text-sm text-gray-500 text-center">
                Agrega algunos platillos deliciosos para comenzar
              </p>
            </div>
          ) : (
            <>
              {/* Cart Items */}
              <div className="p-4">
                {cartItems.map(renderCartItem)}
              </div>

              {/* Order Details */}
              <div className="p-4 bg-white mx-4 mb-4 rounded-xl">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Detalles del Pedido</h3>
                
                {/* Mesa Info */}
                {tableData && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">🪑</span>
                      <div>
                        <p className="text-sm font-semibold text-blue-800">
                          Mesa {tableData.tableNumber}
                        </p>
                        <p className="text-xs text-blue-600">
                          Capacidad: {tableData.capacity} personas
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isMenuWithoutQr ? 'Dirección de envío *' : 'Notas especiales'}
                  </label>
                  {isMenuWithoutQr && (
                    <div className="text-xs text-gray-500 mb-2">
                      Obligatoria para pedidos sin QR.
                    </div>
                  )}
                  <textarea
                    value={specialInstructions}
                    onChange={(e) => setSpecialInstructions(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-base h-20 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={isMenuWithoutQr ? 'Ej: Calle, número, colonia, referencias...' : 'Alergias, preferencias, etc.'}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="bg-white p-5 rounded-b-2xl border-t border-gray-200">
            {/* Usuario autenticado (mostrar perfil y correo) */}
            {user && (
              <div className="flex items-center mb-4">
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName || user.email || 'Usuario'}
                    className="w-9 h-9 rounded-full border border-gray-200 object-cover"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full border border-gray-200 bg-[#f3f4f6] text-[#2C4358] flex items-center justify-center font-bold">
                    {(user.displayName || user.email || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="ml-3">
                  <div className="text-sm font-semibold text-[#2C4358] leading-tight">
                    {user.displayName || 'Usuario'}
                  </div>
                  <div className="text-xs text-[#85929e] leading-tight">
                    {user.email}
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-between items-center mb-4">
              <span className="text-base text-gray-600">
                Total ({getTotalItems()} items):
              </span>
              <span className="text-xl font-bold text-blue-600">
                {formatPrice(getTotalAmount())}
              </span>
            </div>
            
            <button
              onClick={() => handlePlaceOrder()}
              disabled={isPlacingOrder}
              className="w-full bg-[#f7ebd6] text-[#2C4358] py-4 rounded-xl font-bold text-lg hover:bg-[#f0e0c0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-2 border-[#f7ebd6]/50 hover:border-[#f0e0c0]"
            >
              {isPlacingOrder ? 'Enviando Pedido...' : 'Enviar Pedido'}
            </button>
          </div>
        )}
      </div>
      {isPlacingOrder && (
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[55] rounded-2xl">
          <div className="bg-white/90 rounded-2xl px-6 py-5 text-center shadow-lg border border-white/60">
            <div className="w-10 h-10 border-4 border-[#22B6B2]/30 border-t-[#22B6B2] rounded-full animate-spin mx-auto mb-3"></div>
            <div className="text-sm font-semibold text-[#2C4358]">Creando tu pedido...</div>
            <div className="text-xs text-[#85929e]">Por favor espera</div>
          </div>
        </div>
      )}
      {/* Modal de login requerido */}
      {showLoginRequired && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => !isPlacingOrder && setShowLoginRequired(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <img
                src="/assets/ots/Servicio_a_Tiempo_icon_128x128.png"
                alt="Servicio a Tiempo"
                className="w-10 h-10 object-contain"
              />
              <div>
                <h3 className="text-lg font-bold text-[#2C4358]">Inicia sesión para continuar</h3>
                <p className="text-sm text-[#85929e]">Para enviar tu pedido necesitas iniciar sesión.</p>
              </div>
            </div>
            <p className="text-xs text-[#6b7280] mb-4">
              Usamos tu correo solo para confirmar pedidos y mejorar tu experiencia.{' '}
              <a href="/privacidad" className="text-[#22B6B2] font-semibold underline">
                Política de Privacidad
              </a>
              .
            </p>
            <div className="flex gap-3">
              <button
                className="flex-1 px-4 py-3 rounded-xl bg-[#22B6B2] text-white font-bold"
                onClick={async () => {
                  try {
                    await authApiService.signInWithGoogleAndSignUp('comensal');
                setShowLoginRequired(false);
                // Marcar reintento pendiente: un efecto disparará el envío cuando user esté listo
                setPendingAfterLogin(true);
                  } catch (e: any) {
                    setErrorModal({ open: true, title: 'Login requerido', message: e?.message || 'No se pudo iniciar sesión' });
                  }
                }}
              >
                Iniciar sesión con Google
              </button>
              <button
                className="px-4 py-3 rounded-xl border text-[#2C4358]"
                onClick={() => setShowLoginRequired(false)}
                disabled={isPlacingOrder}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
      <ErrorModal isOpen={errorModal.open} title={errorModal.title} message={errorModal.message} details={errorModal.details} onClose={() => setErrorModal({ open: false, message: '' })} />
    </div>
  );
} 